import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { useCountdown } from "../../../components/Data/Countdown";

import { MeterBarWidget } from "./MeterBarWidget";

/**
 * Next-distribution cycle for an active program (drives the countdown +
 * accrued/projected meter). A self-contained structural type (the app's
 * `NextDistribution` from `programMocks` is the identical shape) so the
 * hoisted meter needs no app import.
 */
export interface NextDistribution {
    /** USDv accrued so far toward the next distribution. */
    accrued: number;
    /** USDv projected to be distributed at the next distribution. */
    projected: number;
    /** Length of the distribution cycle, in days (the meter denominator). */
    cycleDays: number;
    /** Seconds remaining until the next distribution (live countdown). */
    inSeconds: number;
}

export interface NextDistributionMeterProps {
    data: NextDistribution;
}

/**
 * NextDistributionMeter — the right-side meter on the active program header:
 * a fill bar that grows as the settlement cycle elapses toward the next
 * distribution, with a live countdown in place of the default `%` readout.
 * Compact (`bottomValues={false}`): just the "Next Distribution" label +
 * countdown over the bar. The accrued / projected USDv figures now live in the
 * `ProgramKeyStats` row (they used to render as this meter's trailing labels).
 *
 * Domain Component over the `MeterBarWidget` GC. The countdown reads off the
 * single shared `useCountdown` hook
 * (`@solomon-apps/shared/src/components/Data/Countdown`) at
 * `precision: "seconds"` for the live `Xd HH:MM:SS` clock — **this is the
 * one piece of live motion in the Viewing-Program read view**.
 *
 * Hoisted into `shared/widgets/yaas/programs` (from the app's
 * `pages/Programs/NextDistributionMeter`) so the shared Viewing-Program read
 * view renders fixture-driven, with no wallet / RPC / orgApiFetch. The app
 * keeps a re-export shim at the old path so `yaas-client` compiles unchanged.
 */
export function NextDistributionMeter({
    data
}: NextDistributionMeterProps): React.ReactElement {
    // `data.inSeconds` is a duration; anchor it to a stable wall-clock
    // target once on mount so the shared hook's per-second tick stays
    // accurate (re-anchoring every render would freeze the clock). Held
    // in state, keyed off `inSeconds`, so a genuinely new distribution
    // re-anchors while renders in between do not.
    const [target, setTarget] = React.useState(
        () => new Date(Date.now() + data.inSeconds * 1000)
    );
    const anchoredFor = React.useRef(data.inSeconds);
    if (anchoredFor.current !== data.inSeconds) {
        anchoredFor.current = data.inSeconds;
        setTarget(new Date(Date.now() + data.inSeconds * 1000));
    }

    const clock = useCountdown(target, { precision: "seconds" });

    const cycleSeconds = data.cycleDays * 86400;
    const remaining = Math.max(0, (target.getTime() - Date.now()) / 1000);
    const elapsed = Math.max(0, cycleSeconds - remaining);
    // The cycle-elapsed fraction drives the bar fill; the live countdown
    // replaces the default `%` readout via `rightSlot`.
    const fraction = elapsed / cycleSeconds;
    return (
        <MeterBarWidget
            title="Next Distribution"
            fraction={fraction}
            bottomValues={false}
            rightSlot={
                <Text
                    variant="fine-print"
                    render={<span />}
                    className="text-foreground-base font-numeric"
                >
                    {clock ?? "00:00:00"}
                </Text>
            }
        />
    );
}
